/**
 * IMP-10 — high-current ion implanter.
 *
 * The only process in the fab that counts its own atoms. Everything else
 * infers what it did from a measurement afterwards; an implanter integrates
 * beam current on a Faraday cup and knows the dose to a per cent. That is why,
 * when a junction comes out wrong, the implanter is usually innocent - and why
 * proving it costs a day either way.
 *
 * WHERE THE IONS STOP
 *
 * An ion entering silicon loses energy to electrons and to nuclei and comes to
 * rest at a projected range Rp, with a spread. Light ions penetrate further for
 * the same energy, which is why boron needs a fraction of the energy arsenic
 * does for the same junction:
 *
 *     Rp ~ scale(species) * E^0.85
 *
 * The distribution about Rp is roughly Gaussian with a straggle of about
 * 38% of the range - so a shallow implant is also a sharp one, and a deep
 * implant is unavoidably smeared.
 *
 * CHANNELING IS THE ONE THAT CATCHES PEOPLE
 *
 * Silicon is a crystal, and along a major axis it is mostly empty space. An ion
 * that enters down a channel travels far further than the amorphous-target
 * model predicts, and it arrives as a deep tail on the profile rather than as a
 * shift of the peak. Tilting the wafer about seven degrees breaks the channel,
 * which is why every implant recipe carries a tilt and a twist, and why an
 * implanter with a drifted tilt stage produces junctions that are the right
 * depth on average and wrong where it matters.
 *
 * AND THE DAMAGE IS NOT OPTIONAL
 *
 * Every ion displaces thousands of lattice atoms on its way in. Past a critical
 * dose the surface layer stops being crystalline at all. That damage has to be
 * annealed out, the anneal moves the dopant, and so this tool's output is
 * the furnace's input - which is the whole reason a junction problem is
 * genuinely hard to assign.
 */

import { clamp } from '../../core/math'

export type Species = 'boron' | 'phosphorus' | 'arsenic' | 'BF2'

/**
 * Range scaling and mass, per species.
 *
 * The scale factors reproduce the standard LSS ranges in silicon: boron travels
 * furthest for its energy, arsenic least.
 */
const SPECIES = {
  boron: { scale: 3.2, mass: 11, damagePerIon: 1 },
  BF2: { scale: 0.72, mass: 49, damagePerIon: 4.4 },
  phosphorus: { scale: 1.35, mass: 31, damagePerIon: 2.8 },
  arsenic: { scale: 0.72, mass: 75, damagePerIon: 6.5 },
} as const

/** Dose above which the implanted layer goes amorphous [ions/cm^2]. */
const AMORPHISATION_DOSE = 1e15

export interface ImplantRecipe {
  species: Species
  /** Acceleration energy [keV]. */
  energyKeV: number
  /** Dose [ions/cm^2]. */
  dose: number
  /** Tilt away from the wafer normal [degrees]. Seven breaks the channel. */
  tiltDeg: number
  /** Twist about the normal [degrees]. */
  twistDeg: number
  /** Beam current [mA]. Throughput, and the thing that heats the wafer. */
  beamCurrentMa: number
  /** Wafer temperature during implant [C]. Hot implants self-anneal as they go. */
  waferTempC: number
}

export const DEFAULT_IMPLANT_RECIPE: ImplantRecipe = {
  species: 'boron',
  energyKeV: 30,
  dose: 3e15,
  tiltDeg: 7,
  twistDeg: 22,
  beamCurrentMa: 10,
  waferTempC: 25,
}

export const IMPLANT_LIMITS: Record<
  'energyKeV' | 'dose' | 'tiltDeg' | 'twistDeg' | 'beamCurrentMa' | 'waferTempC',
  { min: number; max: number; step: number; unit: string; label: string }
> = {
  energyKeV: { min: 1, max: 300, step: 1, unit: 'keV', label: 'Energy' },
  dose: { min: 1e13, max: 1e16, step: 1e13, unit: 'cm⁻²', label: 'Dose' },
  tiltDeg: { min: 0, max: 15, step: 0.5, unit: '°', label: 'Tilt' },
  twistDeg: { min: 0, max: 90, step: 1, unit: '°', label: 'Twist' },
  beamCurrentMa: { min: 0.5, max: 30, step: 0.5, unit: 'mA', label: 'Beam current' },
  waferTempC: { min: -20, max: 400, step: 5, unit: '°C', label: 'Wafer temperature' },
}

export interface ImplantCondition {
  /** Faraday cup calibration, as a fraction. The one number this tool must know. */
  doseCalibration: number
  /** Tilt stage error [degrees]. Small, and it opens the channel. */
  tiltErrorDeg: number
  /** Beam scan uniformity, as a fraction of nominal. */
  scanUniformity: number
  /** Source pressure, as a fraction. Low pressure means an unstable beam. */
  sourcePressure: number
}

export const NOMINAL_IMPLANT_CONDITION: ImplantCondition = {
  doseCalibration: 1,
  tiltErrorDeg: 0,
  scanUniformity: 1,
  sourcePressure: 1,
}

export interface ImplantResult {
  /** Projected range [nm]. */
  rangeNm: number
  /** Straggle, one sigma [nm]. */
  straggleNm: number
  /** Peak concentration [cm^-3]. */
  peakCm3: number
  /** Dose actually delivered [cm^-2]. */
  deliveredDose: number
  /** Fraction of the dose that channelled into a deep tail. */
  channelledFraction: number
  /** Depth the channelled tail reaches [nm]. */
  tailDepthNm: number
  /** Displaced lattice atoms per cm^2, relative to the amorphisation threshold. */
  damageRatio: number
  /** True when the implanted layer has gone fully amorphous. */
  amorphous: boolean
  /** Dose non-uniformity across the wafer [%]. */
  doseNonUniformityPct: number
  /** Time to implant one wafer [s]. */
  cycleSeconds: number
}

/** Projected range in silicon [nm], from the LSS-like fit. */
export const projectedRangeNm = (species: Species, energyKeV: number): number =>
  SPECIES[species].scale * Math.pow(Math.max(energyKeV, 0.1), 0.85)

/**
 * Fraction of the beam that channels.
 *
 * Channeling collapses within a few degrees of the axis, so the dependence on
 * tilt is steep and then flat: at zero tilt a large fraction of the beam goes
 * down a channel, and by seven degrees essentially none does. That shape is why
 * seven is the number on every recipe in the industry.
 */
export function channelledFraction(effectiveTiltDeg: number): number {
  const critical = 3.5
  return clamp(0.42 * Math.exp(-Math.pow(Math.abs(effectiveTiltDeg) / critical, 2)), 0, 1)
}

export function implant(
  recipe: ImplantRecipe,
  condition: ImplantCondition = NOMINAL_IMPLANT_CONDITION,
): ImplantResult {
  const s = SPECIES[recipe.species]
  const rangeNm = projectedRangeNm(recipe.species, recipe.energyKeV)
  const straggleNm = rangeNm * 0.38

  // The stage error is what matters, not the setpoint - a recipe asking for
  // seven degrees on a stage that is two degrees off is a five degree implant.
  const effectiveTilt = recipe.tiltDeg - condition.tiltErrorDeg
  const channelled = channelledFraction(effectiveTilt)
  // Channelled ions travel several times the projected range before stopping.
  const tailDepthNm = rangeNm * 3.4

  const deliveredDose = recipe.dose * clamp(condition.doseCalibration, 0, 2)

  // Gaussian peak: dose divided by the width of the distribution.
  const peakCm3 =
    ((deliveredDose * (1 - channelled)) / (Math.sqrt(2 * Math.PI) * straggleNm * 1e-7)) * 1e0

  // Damage scales with dose and with how heavy the ion is, and a hot wafer
  // anneals some of it away while the implant is still running - which is what
  // a hot implant is FOR.
  const selfAnneal = 1 - clamp((recipe.waferTempC - 25) / 500, 0, 0.6)
  const damageRatio = (deliveredDose * s.damagePerIon * selfAnneal) / AMORPHISATION_DOSE

  const doseNonUniformityPct =
    0.4 / clamp(condition.scanUniformity, 0.1, 2) +
    2.5 * Math.max(0, 1 - clamp(condition.sourcePressure, 0, 2))

  // Implant time is charge divided by current: dose times area times electron
  // charge, over the beam current.
  const waferAreaCm2 = Math.PI * 15 * 15
  const coulombs = (deliveredDose * waferAreaCm2 * 1.602e-19) / 1
  const cycleSeconds = coulombs / (recipe.beamCurrentMa * 1e-3) + 12

  return {
    rangeNm,
    straggleNm,
    peakCm3,
    deliveredDose,
    channelledFraction: channelled,
    tailDepthNm,
    damageRatio,
    amorphous: damageRatio > 1,
    doseNonUniformityPct,
    cycleSeconds,
  }
}

export const IMPLANT_PRESETS: Record<
  string,
  { label: string; note: string; recipe: ImplantRecipe }
> = {
  baseline: {
    label: 'BASELINE-BORON-SD',
    note: 'Source/drain boron: 30 keV, 3e15, seven degrees of tilt to break the channel.',
    recipe: { ...DEFAULT_IMPLANT_RECIPE },
  },
  zeroTilt: {
    label: 'ZERO TILT',
    note:
      'Straight down the channel. The peak lands where it should and a large part of the dose ' +
      'goes three times deeper than anyone intended.',
    recipe: { ...DEFAULT_IMPLANT_RECIPE, tiltDeg: 0 },
  },
  shallow: {
    label: 'SHALLOW EXTENSION',
    note: 'BF2 at 5 keV. Heavy molecule, low energy, very shallow — and far more damage per ion.',
    recipe: { ...DEFAULT_IMPLANT_RECIPE, species: 'BF2', energyKeV: 5, dose: 1e15 },
  },
  deepWell: {
    label: 'DEEP WELL',
    note: 'Phosphorus at 200 keV. Deep, and unavoidably smeared — straggle is 38% of range.',
    recipe: { ...DEFAULT_IMPLANT_RECIPE, species: 'phosphorus', energyKeV: 200, dose: 2e13 },
  },
  hotImplant: {
    label: 'HOT IMPLANT',
    note:
      'Wafer held at 300 C so the lattice repairs itself as the beam goes in. Less damage to ' +
      'anneal out later, which means less thermal budget spent.',
    recipe: { ...DEFAULT_IMPLANT_RECIPE, waferTempC: 300 },
  },
}
